import { useState, type ReactNode } from "react";
import {
  CalendarClock,
  CalendarPlus,
  CalendarX,
  ChevronDown,
  CircleCheck,
  CircleX,
  History,
  Pencil,
  RefreshCw,
  Repeat,
  ShieldCheck,
  UserMinus,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import { Avatar } from "@/modules/escala/components/Avatar";
import { Pill } from "@/modules/escala/components/Pill";
import {
  COR_ICONE_TOM,
  FUNDO_TOM,
  ROTULO_HISTORICO,
  chaveDia,
  diaCurtoComSemana,
  formatarHora,
  rotuloDiaRelativo,
  tempoRelativo,
  type Tom,
} from "@/modules/escala/format";
import type { EscalaHistoricoEvento } from "@/modules/escala/services/escalaService";

type Categoria = "equipe" | "respostas" | "escala";
type Filtro = "tudo" | Categoria;

interface MetaEvento {
  icone: LucideIcon;
  tom: Tom;
  categoria: Categoria;
}

/** Ícone, cor e categoria de filtro de cada um dos 10 tipos de evento. */
const META_EVENTO: Record<string, MetaEvento> = {
  criacao: { icone: CalendarPlus, tom: "brand", categoria: "escala" },
  edicao_nome_descricao: { icone: Pencil, tom: "off", categoria: "escala" },
  edicao_data_hora: { icone: CalendarClock, tom: "info", categoria: "escala" },
  cancelamento: { icone: CalendarX, tom: "no", categoria: "escala" },
  inclusao: { icone: UserPlus, tom: "brand", categoria: "equipe" },
  remocao: { icone: UserMinus, tom: "no", categoria: "equipe" },
  alteracao_funcao: { icone: Repeat, tom: "info", categoria: "equipe" },
  confirmacao: { icone: CircleCheck, tom: "ok", categoria: "respostas" },
  recusa: { icone: CircleX, tom: "no", categoria: "respostas" },
  resposta_administrativa: { icone: ShieldCheck, tom: "info", categoria: "respostas" },
};
const META_DESCONHECIDO: MetaEvento = { icone: History, tom: "off", categoria: "escala" };

const FILTROS: { valor: Filtro; rotulo: string }[] = [
  { valor: "tudo", rotulo: "Tudo" },
  { valor: "equipe", rotulo: "Equipe" },
  { valor: "respostas", rotulo: "Respostas" },
  { valor: "escala", rotulo: "Escala" },
];

const LIMITE_INICIAL = 8;
const LIMITE_PASSO = 10;
const AVATARES_EMPILHADOS = 4;

/**
 * Uma linha da linha do tempo. Inclusões feitas na mesma operação (mesmo
 * instante e mesmo autor — `escala_adicionar_participantes` grava tudo numa
 * transação) viram uma linha só.
 */
interface ItemHistorico {
  chave: string;
  tipo: string;
  criadoEm: string;
  executorId: string | null;
  pessoaIds: string[];
  detalhes: Record<string, unknown>;
  /** Alguma das inclusões do grupo entrou já confirmada (escala sem "exigir confirmação"). */
  confirmacaoAutomatica: boolean;
}

function lerObjeto(valor: unknown): Record<string, unknown> {
  return valor && typeof valor === "object" && !Array.isArray(valor) ? (valor as Record<string, unknown>) : {};
}

function lerData(valor: unknown): string | null {
  return typeof valor === "string" && !Number.isNaN(Date.parse(valor)) ? valor : null;
}

function agrupar(eventos: EscalaHistoricoEvento[]): ItemHistorico[] {
  const ordenados = [...eventos].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  const itens: ItemHistorico[] = [];
  for (const ev of ordenados) {
    const detalhes = lerObjeto(ev.detalhes);
    const automatica = detalhes.confirmacao_automatica === true;
    const ultimo = itens[itens.length - 1];
    if (
      ev.tipo === "inclusao" &&
      ultimo &&
      ultimo.tipo === "inclusao" &&
      ultimo.criadoEm === ev.created_at &&
      ultimo.executorId === ev.executado_por_pessoa_id
    ) {
      if (ev.pessoa_id) ultimo.pessoaIds.push(ev.pessoa_id);
      ultimo.confirmacaoAutomatica = ultimo.confirmacaoAutomatica || automatica;
      continue;
    }
    itens.push({
      chave: ev.id,
      tipo: ev.tipo,
      criadoEm: ev.created_at,
      executorId: ev.executado_por_pessoa_id,
      pessoaIds: ev.pessoa_id ? [ev.pessoa_id] : [],
      detalhes,
      confirmacaoAutomatica: automatica,
    });
  }
  return itens;
}

interface HistoricoEscalaProps {
  eventos: EscalaHistoricoEvento[];
  /** pessoaId → nome, só das pessoas que a RLS de `pessoas` liberou para quem consulta. */
  nomes: Record<string, string>;
  /** Motivo gravado na própria escala (o evento de cancelamento não guarda detalhes). */
  motivoCancelamento: string | null;
}

/**
 * Histórico append-only da escala em linha do tempo, agrupado por dia. Só é
 * chamado depois de `pode_ver_historico_escala` confirmar `true` — participante
 * comum não vê esta tela (arquitetura §14).
 *
 * Mostra só o que o banco guarda: tipo, autor, afetado e os detalhes dos tipos
 * que os têm (data anterior → nova, respostas reiniciadas, confirmação
 * automática, resposta registrada pela liderança). Não repete a justificativa
 * da recusa (ESC-DEC-006) e não inventa "antes e depois" que não existe.
 * Nomes que não foram liberados viram rótulo genérico; nunca se tenta
 * descobri-los por outro caminho.
 */
export function HistoricoEscala({ eventos, nomes, motivoCancelamento }: HistoricoEscalaProps) {
  const [filtro, setFiltro] = useState<Filtro>("tudo");
  const [limite, setLimite] = useState(LIMITE_INICIAL);
  const [abertos, setAbertos] = useState<Record<string, boolean>>({});

  if (eventos.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border px-5 py-9 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-surface-2 text-muted">
          <History size={24} aria-hidden />
        </span>
        <p className="text-[15px] font-bold text-text">Nenhum evento registrado</p>
        <p className="text-sm text-muted">O que acontecer nesta escala vai aparecendo aqui.</p>
      </div>
    );
  }

  const itens = agrupar(eventos);
  const categoriaDe = (item: ItemHistorico) => (META_EVENTO[item.tipo] ?? META_DESCONHECIDO).categoria;
  const contar = (f: Filtro) => (f === "tudo" ? itens.length : itens.filter((i) => categoriaDe(i) === f).length);
  const filtrados = itens.filter((i) => filtro === "tudo" || categoriaDe(i) === filtro);
  const visiveis = filtrados.slice(0, limite);
  const restantes = filtrados.length - visiveis.length;

  const dias: { chave: string; iso: string; itens: ItemHistorico[] }[] = [];
  for (const item of visiveis) {
    const chave = chaveDia(item.criadoEm);
    const dia = dias.find((d) => d.chave === chave);
    if (dia) dia.itens.push(item);
    else dias.push({ chave, iso: item.criadoEm, itens: [item] });
  }

  function mudarFiltro(novo: Filtro) {
    setFiltro(novo);
    setLimite(LIMITE_INICIAL);
  }

  const nomeDe = (id: string | undefined, generico: string) => (id ? (nomes[id] ?? generico) : generico);
  const destaque = (texto: string) => <b className="font-bold">{texto}</b>;

  function titulo(item: ItemHistorico): ReactNode {
    const pessoa = destaque(nomeDe(item.pessoaIds[0], "Um participante"));
    switch (item.tipo) {
      case "criacao":
        return "Escala criada";
      case "edicao_nome_descricao":
        return "Nome ou descrição editados";
      case "edicao_data_hora":
        return "Data e hora alteradas";
      case "cancelamento":
        return "Escala cancelada";
      case "inclusao":
        return item.pessoaIds.length > 1 ? (
          <>
            {destaque(`${item.pessoaIds.length} pessoas`)} entraram na equipe
          </>
        ) : (
          <>{pessoa} entrou na equipe</>
        );
      case "remocao":
        return <>{pessoa} foi removido da equipe</>;
      case "alteracao_funcao":
        return <>Função de {pessoa} alterada</>;
      case "confirmacao":
        return <>{pessoa} confirmou presença</>;
      case "recusa":
        return <>{pessoa} recusou a escala</>;
      case "resposta_administrativa":
        return <>Resposta de {pessoa} registrada pela liderança</>;
      default:
        return ROTULO_HISTORICO[item.tipo] ?? item.tipo;
    }
  }

  function autor(item: ItemHistorico): ReactNode {
    if (!item.executorId) return null;
    if (item.pessoaIds.length === 1 && item.pessoaIds[0] === item.executorId) return "pela própria pessoa";
    const nome = nomes[item.executorId];
    // O próprio título já diz "pela liderança": repetir o fallback genérico só polui.
    if (!nome && item.tipo === "resposta_administrativa") return null;
    return nome ? (
      <>
        por <b className="font-semibold text-text">{nome}</b>
      </>
    ) : (
      "pela liderança"
    );
  }

  function detalhe(item: ItemHistorico): ReactNode {
    if (item.tipo === "edicao_data_hora") {
      const antes = lerData(item.detalhes.data_hora_anterior);
      const agora = lerData(item.detalhes.data_hora_nova);
      // `respostas_reiniciadas` é a contagem gravada pelo banco (aceita também booleano).
      const reiniciadas = item.detalhes.respostas_reiniciadas;
      const houveReinicio = reiniciadas === true || (typeof reiniciadas === "number" && reiniciadas > 0);
      return (
        <>
          {(antes || agora) && (
            <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-border text-[13px]">
              <div className="bg-surface-2 px-3 py-2">
                <span className="block text-[10.5px] font-extrabold uppercase tracking-wider text-muted">Antes</span>
                <span className="text-muted line-through">{antes ? `${diaCurtoComSemana(antes)} · ${formatarHora(antes)}` : "—"}</span>
              </div>
              <div className="border-l border-border bg-surface px-3 py-2">
                <span className="block text-[10.5px] font-extrabold uppercase tracking-wider text-muted">Agora</span>
                <span className="font-semibold text-text">{agora ? `${diaCurtoComSemana(agora)} · ${formatarHora(agora)}` : "—"}</span>
              </div>
            </div>
          )}
          {houveReinicio && (
            <span>
              <Pill tom="info" icone={RefreshCw}>
                Respostas reiniciadas
              </Pill>
            </span>
          )}
        </>
      );
    }
    if (item.tipo === "cancelamento" && motivoCancelamento) {
      return (
        <p className="break-words rounded-xl border border-danger/30 bg-danger/5 px-3 py-2 text-[13.5px] text-text">
          <span className="block text-[10.5px] font-extrabold uppercase tracking-wider text-muted">Motivo</span>
          {motivoCancelamento}
        </p>
      );
    }
    if (item.tipo === "inclusao") {
      const nomesDoGrupo = item.pessoaIds.map((id) => nomeDe(id, "Participante"));
      const aberto = !!abertos[item.chave];
      return (
        <>
          {item.pessoaIds.length > 1 && (
            <>
              <div className="flex items-center gap-2">
                <span className="flex">
                  {nomesDoGrupo.slice(0, AVATARES_EMPILHADOS).map((nome, i) => (
                    <span key={`${item.chave}-${i}`} className="-ml-1.5 rounded-full ring-2 ring-bg first:ml-0">
                      <Avatar nome={nome} tamanho="sm" />
                    </span>
                  ))}
                  {nomesDoGrupo.length > AVATARES_EMPILHADOS && (
                    <span
                      aria-hidden
                      className="-ml-1.5 grid h-8 w-8 place-items-center rounded-full bg-surface-2 text-[11px] font-bold text-muted ring-2 ring-bg"
                    >
                      +{nomesDoGrupo.length - AVATARES_EMPILHADOS}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => setAbertos((a) => ({ ...a, [item.chave]: !a[item.chave] }))}
                  aria-expanded={aberto}
                  className="inline-flex min-h-8 items-center gap-1 text-[12.5px] font-bold text-text"
                >
                  {aberto ? "Ocultar nomes" : "Ver quem entrou"}
                  <ChevronDown size={14} aria-hidden className={`text-primary ${aberto ? "rotate-180" : ""}`} />
                </button>
              </div>
              {aberto && (
                <ul className="flex flex-wrap gap-1.5">
                  {nomesDoGrupo.map((nome, i) => (
                    <li
                      key={`${item.chave}-nome-${i}`}
                      className="rounded-full bg-surface-2 px-2.5 py-1 text-[12.5px] font-semibold text-text"
                    >
                      {nome}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {item.confirmacaoAutomatica && (
            <span>
              <Pill tom="ok" icone={CircleCheck}>
                Confirmada automaticamente ao entrar
              </Pill>
            </span>
          )}
        </>
      );
    }
    if (item.tipo === "resposta_administrativa") {
      const resposta = item.detalhes.resposta;
      if (resposta === "CONFIRMADA")
        return (
          <span>
            <Pill tom="ok" icone={CircleCheck}>
              Confirmada
            </Pill>
          </span>
        );
      if (resposta === "RECUSADA")
        return (
          <span>
            <Pill tom="no" icone={CircleX}>
              Recusada
            </Pill>
          </span>
        );
    }
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <p className="flex items-center gap-1.5 text-[13px] text-muted">
          <History size={15} aria-hidden />
          {itens.length} {itens.length === 1 ? "registro" : "registros"} · último {tempoRelativo(itens[0]!.criadoEm)}
        </p>
        <div role="group" aria-label="Filtrar histórico" className="flex flex-wrap gap-2">
          {FILTROS.map((f) => (
            <button
              key={f.valor}
              type="button"
              onClick={() => mudarFiltro(f.valor)}
              aria-pressed={filtro === f.valor}
              className={`inline-flex min-h-10 items-center gap-1.5 rounded-full border px-3.5 text-[13.5px] font-semibold transition-colors ${
                filtro === f.valor
                  ? "border-primary bg-primary/10 text-text"
                  : "border-border bg-surface text-muted hover:bg-surface-2 hover:text-text"
              }`}
            >
              {f.rotulo}
              <span className="text-xs font-bold text-muted">{contar(f.valor)}</span>
            </button>
          ))}
        </div>
      </div>

      {dias.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border px-4 py-6 text-center text-sm font-semibold text-text">
          Nada nesta categoria
        </p>
      ) : (
        dias.map((dia) => {
          const rotulo = rotuloDiaRelativo(dia.iso);
          const relativo = rotulo === "Hoje" || rotulo === "Ontem";
          return (
            <section key={dia.chave} aria-label={rotulo} className="flex flex-col gap-3">
              <div className="flex items-baseline justify-between gap-2 border-b border-border pb-1.5">
                <h3 className="text-[13px] font-extrabold uppercase tracking-wider text-muted">{rotulo}</h3>
                {relativo && <span className="text-xs text-muted">{diaCurtoComSemana(dia.iso)}</span>}
              </div>
              <ol>
                {dia.itens.map((item) => {
                  const meta = META_EVENTO[item.tipo] ?? META_DESCONHECIDO;
                  const Icone = meta.icone;
                  const extra = detalhe(item);
                  const quem = autor(item);
                  return (
                    <li key={item.chave} className="group relative flex gap-3 pb-4 last:pb-0">
                      <span
                        aria-hidden
                        className="absolute bottom-0 left-4 top-9 w-0.5 -translate-x-1/2 bg-border group-last:hidden"
                      />
                      <span
                        aria-hidden
                        className={`relative grid h-8 w-8 shrink-0 place-items-center rounded-full ${FUNDO_TOM[meta.tom]}`}
                      >
                        <Icone size={16} className={COR_ICONE_TOM[meta.tom]} />
                      </span>
                      <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-1">
                        <p className="break-words text-[14.5px] leading-snug text-text">{titulo(item)}</p>
                        {extra}
                        <p className="text-[12.5px] text-muted">
                          {quem && <>{quem} · </>}
                          {formatarHora(item.criadoEm)} · {tempoRelativo(item.criadoEm)}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          );
        })
      )}

      {restantes > 0 && (
        <button
          type="button"
          onClick={() => setLimite((l) => l + LIMITE_PASSO)}
          className="mx-auto min-h-11 rounded-xl border border-border px-4 text-sm font-semibold text-text transition-colors hover:bg-surface-2"
        >
          Mostrar mais antigos ({restantes})
        </button>
      )}
    </div>
  );
}
